const mobileSwiperInstances = new Map();
const mobileSwiperSubscriptions = new Set();
function destroyMobileSwiperInstance(sectionId) {
  const existing = mobileSwiperInstances.get(sectionId);
  if (!existing) return;

  existing.main?.destroy?.(true, true);
  existing.thumbs?.destroy?.(true, true);
  mobileSwiperInstances.delete(sectionId);
}

function finalizeMobileSwiper(sectionId, mainSwiper, thumbSwiper, preservedIndex, logLabel = "init") {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      mainSwiper.allowTouchMove = true;
      thumbSwiper.allowTouchMove = true;

      mainSwiper.update();
      thumbSwiper.update();

      // Rebind touch/mouse handlers in case modal focus/overlay teardown detached them.
      mainSwiper.detachEvents?.();
      thumbSwiper.detachEvents?.();
      mainSwiper.attachEvents?.();
      thumbSwiper.attachEvents?.();

      mainSwiper.updateSlidesClasses();
      thumbSwiper.updateSlidesClasses();

    });
  });
}

function getMobileSwiperInitialIndex(sectionId, mainEl, preferredMediaId = null) {
  if (!mainEl || !preferredMediaId) return 0;

  const slides = Array.from(
    mainEl.querySelectorAll(".swiper-slide[data-media-id]"),
  );
  const targetMediaId = `${sectionId}-${preferredMediaId}`;
  const matchedIndex = slides.findIndex(
    (slide) => slide.dataset.mediaId === targetMediaId,
  );

  return matchedIndex >= 0 ? matchedIndex : 0;
}

function rebuildMobileSwiper(
  sectionId,
  { preserveActiveIndex = true, preferredMediaId = null } = {},
) {
  if (window.innerWidth >= 825 || typeof Swiper === "undefined") return;

  const mainEl = document.querySelector(`#swiper-mobile-media-${sectionId}`);
  const thumbsEl = document.querySelector(
    `#swiper-mobile-thumbs-${sectionId}`,
  );

  if (!mainEl || !thumbsEl) return;

  const initialSlide = preserveActiveIndex
    ? mobileSwiperInstances.get(sectionId)?.main?.activeIndex ??
      mainEl.swiper?.activeIndex ??
      0
    : getMobileSwiperInitialIndex(sectionId, mainEl, preferredMediaId);

  const galleryScope =
    mainEl.closest(".desktop-view") || mainEl.parentElement;
  const nextEl = galleryScope?.querySelector(".swiper-button-next") || null;
  const prevEl = galleryScope?.querySelector(".swiper-button-prev") || null;

  destroyMobileSwiperInstance(sectionId);

  if (mainEl.swiper) mainEl.swiper.destroy(true, true);
  if (thumbsEl.swiper) thumbsEl.swiper.destroy(true, true);

  const thumbSwiper = new Swiper(thumbsEl, {
    slidesPerView: "auto",
    spaceBetween: 8,
    watchSlidesProgress: true,
    slideToClickedSlide: true,
  });

  const mainSwiper = new Swiper(mainEl, {
    initialSlide,
    slidesPerView: 1,
    navigation: {
      nextEl: mainEl.querySelector(".swiper-button-next"),
      prevEl: mainEl.querySelector(".swiper-button-prev"),
    },
    thumbs: {
      swiper: thumbSwiper,
    },
  });

  if (nextEl && prevEl && mainSwiper.navigation) {
    mainSwiper.params.navigation.nextEl = nextEl;
    mainSwiper.params.navigation.prevEl = prevEl;
    mainSwiper.navigation.destroy();
    mainSwiper.navigation.init();
    mainSwiper.navigation.update();
  }

  mobileSwiperInstances.set(sectionId, {
    main: mainSwiper,
    thumbs: thumbSwiper,
  });

  finalizeMobileSwiper(
    sectionId,
    mainSwiper,
    thumbSwiper,
    initialSlide,
    "init",
  );
}

function initMobileSwiper(sectionId) {
  if (window.innerWidth >= 825 || typeof Swiper === "undefined") return;
  if (typeof subscribe !== "function") return;

  rebuildMobileSwiper(sectionId);

  if (!mobileSwiperSubscriptions.has(sectionId)) {
    mobileSwiperSubscriptions.add(sectionId);

    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (data.sectionId !== sectionId) return;

      requestAnimationFrame(() => {
        rebuildMobileSwiper(sectionId, {
          preserveActiveIndex: false,
          preferredMediaId: data.variant?.featured_media?.id ?? null,
        });
      });
    });
  }

  return;

  const initSwiper = () => {
    const mainEl = document.querySelector(`#swiper-mobile-media-${sectionId}`);
    const thumbsEl = document.querySelector(
      `#swiper-mobile-thumbs-${sectionId}`,
    );

    if (!mainEl || !thumbsEl) return;

    const preservedIndex =
      mobileSwiperInstances.get(sectionId)?.main?.activeIndex ??
      mainEl.swiper?.activeIndex ??
      0;

    const galleryScope =
      mainEl.closest(".desktop-view") || mainEl.parentElement;
    const nextEl = galleryScope?.querySelector(".swiper-button-next") || null;
    const prevEl = galleryScope?.querySelector(".swiper-button-prev") || null;

    destroyMobileSwiperInstance(sectionId);

    // Clean up stale swiper references left on the DOM after modal open/close.
    if (mainEl.swiper) mainEl.swiper.destroy(true, true);
    if (thumbsEl.swiper) thumbsEl.swiper.destroy(true, true);

    // Thumbs swiper FIRST
    const thumbSwiper = new Swiper(thumbsEl, {
      slidesPerView: "auto",
      spaceBetween: 8,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
    });

    // Main media swiper
    const mainSwiper = new Swiper(mainEl, {
      initialSlide: preservedIndex,
      slidesPerView: 1,
      navigation: {
        nextEl: mainEl.querySelector(".swiper-button-next"), // ✅ Matches image
        prevEl: mainEl.querySelector(".swiper-button-prev"),
      },
      thumbs: {
        swiper: thumbSwiper, // ✅ Thumbs sync
      },
    });

    if (nextEl && prevEl && mainSwiper.navigation) {
      mainSwiper.params.navigation.nextEl = nextEl;
      mainSwiper.params.navigation.prevEl = prevEl;
      mainSwiper.navigation.destroy();
      mainSwiper.navigation.init();
      mainSwiper.navigation.update();
    }

    mobileSwiperInstances.set(sectionId, {
      main: mainSwiper,
      thumbs: thumbSwiper,
    });

    finalizeMobileSwiper(
      sectionId,
      mainSwiper,
      thumbSwiper,
      preservedIndex,
      "init",
    );
  };

  // Initial init
  initSwiper();

  // Re-init on variant change (matches your image structure)
  subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
    if (data.sectionId !== sectionId) return;
    requestAnimationFrame(initSwiper); // ✅ Dawn DOM swap timing
  });
}

document.body.addEventListener("product:media-modal-closed", (event) => {
  if (window.innerWidth >= 825 || typeof Swiper === "undefined") return;

  const sectionId = event.detail?.sectionId;
  if (!sectionId) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      rebuildMobileSwiper(sectionId, { preserveActiveIndex: true });
      return;

      const mainEl = document.querySelector(
        `#swiper-mobile-media-${sectionId}`,
      );
      const thumbsEl = document.querySelector(
        `#swiper-mobile-thumbs-${sectionId}`,
      );
      const preservedIndex =
        mobileSwiperInstances.get(sectionId)?.main?.activeIndex ??
        mainEl?.swiper?.activeIndex ??
        0;

      if (!mainEl || !thumbsEl) return;

      const galleryScope =
        mainEl.closest(".desktop-view") || mainEl.parentElement;
      const nextEl = galleryScope?.querySelector(".swiper-button-next") || null;
      const prevEl = galleryScope?.querySelector(".swiper-button-prev") || null;

      destroyMobileSwiperInstance(sectionId);
      if (mainEl.swiper) mainEl.swiper.destroy(true, true);
      if (thumbsEl.swiper) thumbsEl.swiper.destroy(true, true);

      const thumbSwiper = new Swiper(thumbsEl, {
        slidesPerView: "auto",
        spaceBetween: 8,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
      });

      const mainSwiper = new Swiper(mainEl, {
        initialSlide: preservedIndex,
        slidesPerView: 1,
        navigation: {
          nextEl: mainEl.querySelector(".swiper-button-next"),
          prevEl: mainEl.querySelector(".swiper-button-prev"),
        },
        thumbs: {
          swiper: thumbSwiper,
        },
      });

      if (nextEl && prevEl && mainSwiper.navigation) {
        mainSwiper.params.navigation.nextEl = nextEl;
        mainSwiper.params.navigation.prevEl = prevEl;
        mainSwiper.navigation.destroy();
        mainSwiper.navigation.init();
        mainSwiper.navigation.update();
      }

      mobileSwiperInstances.set(sectionId, {
        main: mainSwiper,
        thumbs: thumbSwiper,
      });

      finalizeMobileSwiper(
        sectionId,
        mainSwiper,
        thumbSwiper,
        preservedIndex,
        "product media modal close",
      );
    });
  });
});

if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");

        this.wishlistUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.wishlistUpdate,
          this.updateWishlist.bind(this),
        );
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.autoSelectVariantFromLocal();

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true }),
        );
        
        // ─── 1. Initial sync when modal is opened (keep this) ───────
        const modalObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'open' && mutation.target.hasAttribute('open')) {
              const modal = mutation.target;
              syncModalWithMainPDP(modal);
            }
          });
        });

        document.querySelectorAll('modal-dialog[id^="StickyVariantModal-"]').forEach(modal => {
          modalObserver.observe(modal, { attributes: true });
        });

        // ─── 2. Sync function (extracted so we can call it from multiple places) ───────
        function syncModalWithMainPDP(modal) {
          if (!modal || !modal.hasAttribute('open')) return;

          const mainSizePicker   = document.querySelector('product-info .size-picker');
          const mainLengthPicker = document.querySelector('product-info .length-picker');

          const modalSizePicker   = modal.querySelector('.size-picker');
          const modalLengthPicker = modal.querySelector('.length-picker');

          // Size
          if (mainSizePicker && modalSizePicker) {
            const mainChecked = mainSizePicker.querySelector('input[type="radio"]:checked:not(.toggle-radio)');
            if (mainChecked) {
              const targetValue = mainChecked.value;
              const modalInput = modalSizePicker.querySelector(`input[value="${targetValue}"]`);
              if (modalInput && !modalInput.checked) {
                modalInput.checked = true;
                modalInput.setAttribute('checked', 'checked');
                modalInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }

          // Length – same logic
          if (mainLengthPicker && modalLengthPicker) {
            const mainChecked = mainLengthPicker.querySelector('input[type="radio"]:checked');
            if (mainChecked) {
              const targetValue = mainChecked.value;
              const modalInput = modalLengthPicker.querySelector(`input[value="${targetValue}"]`);
              if (modalInput && !modalInput.checked) {
                modalInput.checked = true;
                modalInput.setAttribute('checked', 'checked');
                modalInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }

          // ─── Force correct text in selected spans after sync ───────
          const sizeSpan   = modal.querySelector('#selected-size');
          const lengthSpan = modal.querySelector('#selected-length');

          if (sizeSpan && modalSizePicker) {
            const checkedSize = modalSizePicker.querySelector('input[type="radio"]:checked:not(.toggle-radio)');
            if (checkedSize) {
              let displayValue = checkedSize.value.trim();
              
              // Simple formatting (adjust if your values need special cleaning)
              displayValue = displayValue
                .replace(/[^0-9a-zA-Z\s-]/g, '')   // remove weird chars
                .trim();

              sizeSpan.textContent = displayValue;
              sizeSpan.style.display = '';   
            }
          }

          if (lengthSpan && modalLengthPicker) {
            const checkedLength = modalLengthPicker.querySelector('input[type="radio"]:checked');
            if (checkedLength) {
              let displayValue = checkedLength.value.trim();
              
              displayValue = displayValue
                .replace(/[^0-9a-zA-Z\s-]/g, '')
                .trim();

              lengthSpan.textContent = displayValue;
              lengthSpan.style.display = '';
            }
          }
        }

        // ─── 3. NEW: Listen to main page changes and push to open modal ───────
        document.addEventListener('change', (e) => {
          if (e.target.type !== 'radio') return;

          // Only care about main page changes (ignore modal radios here)
          const isMainPageChange = !e.target.closest('modal-dialog');
          if (!isMainPageChange) return;

          // Find currently open modal
          const openModal = document.querySelector('modal-dialog[id^="StickyVariantModal-"][open]');
          if (openModal) {
            if (openModal) {
            const errorEl = openModal.querySelector('.sticky-error-message');
            if (errorEl) {
              errorEl.textContent = '';
              errorEl.style.display = 'none';
            }
          }
            syncModalWithMainPDP(openModal);
          }
        }, true);
      }

      // Order history page buy again button redirect to the pdp page with values
      autoSelectVariantFromLocal() {
        const storedOptionsStr = localStorage.getItem("buyAgainOptions");
        if (!storedOptionsStr) return;

        const storedOptions = JSON.parse(storedOptionsStr);
        localStorage.removeItem("buyAgainOptions");

        // Now apply the selection using stored values
        let selected = false;

        const urlParams = new URLSearchParams(window.location.search);
        const variantIdFromUrl = urlParams.get("variant");
        if (variantIdFromUrl) {
          const inputById = this.querySelector(
            `input[value="${variantIdFromUrl}"]`,
          );
          if (inputById && inputById.type === "radio") {
            inputById.checked = true;
            inputById.dispatchEvent(new Event("change", { bubbles: true }));
            selected = true;
          }
        }

        if (!selected) {
          const targetSize = storedOptions.size;
          const targetLength = storedOptions.length;
          const targetColor = storedOptions.color;

          // COLOR
          const colorPicker = this.querySelector(".color-picker");
          if (colorPicker) {
            const colorInput = colorPicker.querySelector(
              `input[type="radio"][value="${targetColor}"]`,
            );
            if (colorInput) {
              colorInput.checked = true;
              colorInput.dispatchEvent(new Event("change", { bubbles: true }));
            }
          }

          // SIZE
          const sizePicker = this.querySelector(".size-picker");
          if (sizePicker) {
            const sizeInput = sizePicker.querySelector(
              `input[type="radio"][value="${targetSize}"]`,
            );
            if (sizeInput) {
              sizeInput.checked = true;
              sizeInput.dispatchEvent(new Event("change", { bubbles: true }));
            }
          }

          // LENGTH
          const lengthPicker = this.querySelector(".length-picker");
          if (lengthPicker) {
            const lengthInput = lengthPicker.querySelector(
              `input[type="radio"][value="${targetLength}"]`,
            );
            if (lengthInput) {
              lengthInput.checked = true;
              lengthInput.dispatchEvent(new Event("change", { bubbles: true }));
            }
          }

          const variantSelects = this.querySelector("variant-selects");
          if (variantSelects) {
            variantSelects.dispatchEvent(
              new Event("change", { bubbles: true }),
            );
          }
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.wishlistUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel"),
            ),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

       attemptAutoSubmit(variantId = null) {
        // Modal detection + validation
        const modal = document.querySelector(
          'modal-dialog[id^="StickyVariantModal-"]:not(.hidden)',
        );
        if (!modal) return; 

        // Scoped validation (requires <product-form> from add-bag-modal.liquid update)
        const modalFormEl = modal.querySelector("product-form");
        if (!modalFormEl || !modalFormEl.isAllVariantsSelected()) {
          return;
        }

        const modalVariantId = modal.querySelector('input[name="id"]')?.value;
       
        // Confirm available variant
        const variantJson = modal.querySelector("[data-selected-variant]");
        if (!variantJson) {
          return;
        }
        const variant = JSON.parse(variantJson.textContent);

        // Use passed variantId if available, otherwise fallback to JSON
        const finalVariantId = variantId || variant?.id;

        if (!variant.available) {
          return;
        }

        const form = modal.querySelector('form[data-type="add-to-cart-form"]');
        if (!form) return;

        // Force update the input just in case
        const variantInput = form.querySelector(".product-variant-id");
        if (variantInput && finalVariantId) {
          variantInput.value = finalVariantId;
        }

        // Dispatch submit (simulates button click, but bubbles to handler)
        form.dispatchEvent(
          new SubmitEvent("submit", {
            bubbles: true,
            cancelable: true,
            submitter: form.querySelector('button[type="submit"]'), // Mimic real submit button
          }),
        );
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;
        if (event.target.name === "sizepanel-measurement") return;

        this.resetProductFormState();

        const editModal = this.closest(".quick-update-btn");
        if (editModal) {
          const oldVariantDiv =
            editModal.querySelector("#old-variant-id") ||
            this.querySelector("#old-variant-id");
          const oldVariantId = oldVariantDiv
            ? oldVariantDiv.textContent.trim()
            : null;

          if (oldVariantId) {
            // --- Read the full variants array from the embedded JSON blob ---
            const variantSelects = this.querySelector("variant-selects");
            const variantsScript = variantSelects?.querySelector(
              "script[data-product-variants]"
            );

            if (variantsScript) {
              try {
                const allVariants = JSON.parse(variantsScript.textContent);

                // Collect the currently selected values for each option
                // (option1, option2, option3 … correspond to position order)
                const selectedOptions = [];
                variantSelects
                  .querySelectorAll("fieldset.product-form__input")
                  .forEach((fieldset) => {
                    const checked = fieldset.querySelector(
                      'input[type="radio"]:checked:not(.toggle-radio)'
                    );
                    if (checked) selectedOptions.push(checked.value);
                  });

                // Find the matching variant
                const matched = allVariants.find((v) => {
                  const opts = [v.option1, v.option2, v.option3].filter(Boolean);
                  return selectedOptions.every((sel, i) => opts[i] === sel);
                });

                if (matched) {
                  // Write the resolved ID directly — Update button is now safe
                  // to fire at any time with no race condition.
                  editModal
                    .querySelectorAll(".update-button-quick-modal")
                    .forEach((btn) => {
                      btn.dataset.variantId = String(matched.id);
                      btn.disabled = false;
                      btn.classList.remove("disabled");
                    });
                  // Signal to update-button.js that no fetch-await is needed
                  window._pendingVariantFetch = null;
                }
              } catch (e) {
                // JSON parse failed — fall back to fetch-based resolution
              }
            }

            // Fallback: still enable button even if JSON match failed
            editModal.querySelectorAll(".update-button-quick-modal").forEach((btn) => {
              btn.disabled = false;
              btn.classList.remove("disabled");
            });
          }
        }
        // ─────────────────────────────────────────────────────────────────────


        // Smart Variant Resolution: find the guaranteed valid variant before fetching
        let targetVariantId = null;
        const variantSelects = this.querySelector("variant-selects");
        const variantsScript = variantSelects?.querySelector(
          "script[data-product-variants]"
        );

        if (variantsScript) {
          try {
            const allVariants = JSON.parse(variantsScript.textContent);
            const resolved = this.resolveBestVariant(allVariants, variantSelects, target);
            if (resolved) {
              targetVariantId = resolved.id;
            }
          } catch (e) {
            console.error("Variant resolve error:", e);
          }
        }

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;
        const sizeErrorEl = this.querySelector(
          ".product-form__error-message-wrapper-variant",
        );

        if (sizeErrorEl) sizeErrorEl.style.display = "none";

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
            targetVariantId
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            if (this.dataset.originalSection) {
              // Fix: Skip replacing the ENTIRE product-info wrapper inside the modal.
              // All necessary sub-components (price, images, variant-pickers, buttons)
              // are already explicitly updated individually above. Swapping the entire
              // module DOM tree after the fact is what causes the 500ms browser freeze.
            } else {
              HTMLUpdateUtility.viewTransition(
                this,
                html.querySelector("product-info"),
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks,
              );
            }
          }
          this.attemptAutoSubmit(variant?.id);
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        window._variantFetchCounter = (window._variantFetchCounter || 0) + 1;
        const myFetchId = window._variantFetchCounter;

        const activeModal = this.closest(".quick-update-btn");
        if (activeModal) {
          activeModal
            .querySelectorAll(".update-button-quick-modal")
            .forEach((btn) => {
              btn.disabled = true;
              btn.classList.add("disabled", "loading");
            });
        }

        // ─── INSTANT CACHE: Skip parsing huge DOM trees ──────────
        window._variantHTMLCache = window._variantHTMLCache || new Map();
        if (window._variantHTMLCache.has(requestUrl)) {
          const cachedHTML = window._variantHTMLCache
            .get(requestUrl)
            .cloneNode(true);
          callback(cachedHTML);
          this.querySelector(`#${targetId}`)?.focus();
          window._pendingVariantFetch = null;
          return;
        }

        window._pendingVariantFetch = fetch(requestUrl, {
          signal: this.abortController.signal,
        })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            window._variantHTMLCache.set(requestUrl, html.cloneNode(true));
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            this.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          })
          .finally(() => {
            // Only the LATEST fetch is allowed to clear the global promise.
            // If a newer fetch started after this one, leave the promise intact.
            if (window._variantFetchCounter === myFetchId) {
              window._pendingVariantFetch = null;
            }
          });
        // ─────────────────────────────────────────────────────────────────────
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]",
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getOptionPosition(input, fieldsetIndex, optionPositions) {
        if (!input) return fieldsetIndex + 1;
        // Method 1: extract from name like "color-1-product-form-..."
        const match = (input.name || "").match(/-(\d+)-/);
        if (match) return parseInt(match[1], 10);

        // Method 2: extract from data-option-positions
        if (optionPositions) {
          const rawName = (input.name || "").split("-")[0].trim().toLowerCase();
          if (optionPositions[rawName]) return optionPositions[rawName];
        }

        // Method 3: fieldset index fallback
        return fieldsetIndex + 1;
      }

      resolveBestVariant(allVariants, variantSelects, targetInput) {
        if (!allVariants || !allVariants.length || !targetInput) return null;

        const normalize = (s) => (s ? String(s).trim().toLowerCase() : "");

        let optionPositions = null;
        const posScript = variantSelects.querySelector("script[data-option-positions]");
        if (posScript) {
          try {
            optionPositions = JSON.parse(posScript.textContent);
          } catch (e) {}
        }

        // 1. Collect currently selected option values by option position
        const selectedByPos = {};
        let targetPos = null;

        const fieldsets = variantSelects.querySelectorAll("fieldset.product-form__input");
        fieldsets.forEach((fieldset, idx) => {
          const isTargetFs = fieldset.contains(targetInput);
          const sampleInput = isTargetFs
            ? targetInput
            : fieldset.querySelector('input[type="radio"]:not(.toggle-radio)');
          const pos = this.getOptionPosition(sampleInput, idx, optionPositions);

          if (isTargetFs) {
            targetPos = pos;
            selectedByPos[pos] = targetInput.value;
          } else {
            const checked = fieldset.querySelector(
              'input[type="radio"]:checked:not(.toggle-radio)'
            );
            if (checked) {
              selectedByPos[pos] = checked.value;
            }
          }
        });

        if (!targetPos) {
          targetPos = this.getOptionPosition(targetInput, 0, optionPositions);
        }
        const targetVal = targetInput.value;

        // Check 1: Exact positional match (option1, option2, option3)
        const exactMatch = allVariants.find((v) => {
          for (const pos in selectedByPos) {
            const val = selectedByPos[pos];
            if (val && normalize(v["option" + pos]) !== normalize(val)) {
              return false;
            }
          }
          return true;
        });
        if (exactMatch) return exactMatch;

        // Check 2: Must match targetPos + targetVal, and Color (option1) if present
        const colorVal = selectedByPos[1];
        let candidates = allVariants.filter((v) => {
          const matchesTarget = normalize(v["option" + targetPos]) === normalize(targetVal);
          const matchesColor = !colorVal || normalize(v["option1"]) === normalize(colorVal);
          return matchesTarget && matchesColor;
        });

        if (candidates.length > 0) {
          return candidates.find((v) => v.available) || candidates[0];
        }

        // Check 3: If no candidates with color, match target position alone
        candidates = allVariants.filter(
          (v) => normalize(v["option" + targetPos]) === normalize(targetVal)
        );
        if (candidates.length > 0) {
          return candidates.find((v) => v.available) || candidates[0];
        }

        return null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
        variantId = null,
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (variantId) {
          params.push(`variant=${variantId}`);
        } else if (optionValues && optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        let modalIsOpen = false;

        document.querySelectorAll(".quick-update-btn").forEach((radio) => {
          if (radio.hasAttribute("open")) {
            modalIsOpen = true;
          }
        });

        if (modalIsOpen) {
          // Removed console.log("is open data"); to clear console noise
        } else {
          // FIX: Use this.querySelector to scope to current product-info element
          // instead of document.querySelector which grabs the first one on the page
          const oldvariantSelects = this.querySelector("variant-selects");

          // Safety check - if no variant selects found in this element, skip
          if (!oldvariantSelects) {
            if (variantSelects) {
              HTMLUpdateUtility.viewTransition(
                this.variantSelectors,
                variantSelects,
                this.preProcessHtmlCallbacks,
              );
            }
            return;
          }

          // Keep fieldsets checked state properly synchronized
          variantSelects
            .querySelectorAll("fieldset.product-form__input")
            .forEach((newFieldset) => {
              const isColor = newFieldset.classList.contains("color-picker");
              const isSize = newFieldset.classList.contains("size-picker");
              const isLength = newFieldset.classList.contains("length-picker");

              let oldFieldset = null;
              if (isColor) oldFieldset = oldvariantSelects.querySelector(".color-picker");
              else if (isSize) oldFieldset = oldvariantSelects.querySelector(".size-picker");
              else if (isLength) oldFieldset = oldvariantSelects.querySelector(".length-picker");
              else {
                const legend = newFieldset.querySelector("legend")?.textContent?.trim();
                oldFieldset = Array.from(oldvariantSelects.querySelectorAll("fieldset.product-form__input"))
                  .find((f) => f.querySelector("legend")?.textContent?.trim() === legend);
              }

              const oldCheckedRadio = oldFieldset?.querySelector('input[type="radio"]:checked:not(.toggle-radio)');
              const hadSelectionInOld = !!oldCheckedRadio;

              if (!hadSelectionInOld && !isColor) {
                // If user hadn't selected anything in this non-color fieldset on initial load, keep it unchecked
                // UNLESS this fieldset has only 1 radio (single size / single length), in which case it must be checked!
                const visibleRadios = Array.from(
                  newFieldset.querySelectorAll('input[type="radio"]:not(.toggle-radio)')
                ).filter((r) => r.offsetParent !== null);

                if (visibleRadios.length > 1) {
                  newFieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
                    radio.checked = false;
                    radio.removeAttribute("checked");
                  });
                } else if (visibleRadios.length === 1) {
                  visibleRadios[0].checked = true;
                  visibleRadios[0].setAttribute("checked", "checked");
                }
              } else if (oldCheckedRadio) {
                // User had a selection. Does that value still exist in the new options?
                const matchingNewRadio = Array.from(newFieldset.querySelectorAll('input[type="radio"]'))
                  .find((r) => r.value === oldCheckedRadio.value);
                if (matchingNewRadio) {
                  newFieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
                    if (radio === matchingNewRadio) {
                      radio.checked = true;
                      radio.setAttribute("checked", "checked");
                    } else {
                      radio.checked = false;
                      radio.removeAttribute("checked");
                    }
                  });
                } else {
                  // The old selection doesn't exist for the new variant (e.g. Length 32 deleted for Waist 29)
                  // Keep whatever Liquid marked as checked for this valid variant!
                  const liquidChecked = newFieldset.querySelector('input[type="radio"][checked]');
                  if (liquidChecked) {
                    liquidChecked.checked = true;
                  }
                }
              }
            });

          const sizeCheckedInput = variantSelects.querySelector(
            '.size-picker input[type="radio"]:checked:not(.toggle-radio)',
          );
          variantSelects.querySelectorAll("#selected-size").forEach((span) => {
            if (sizeCheckedInput) {
              span.textContent = " " + sizeCheckedInput.value;
              span.style.display = "inline";
            } else {
              span.style.display = "none";
            }
          });

          const lengthCheckedInput = variantSelects.querySelector(
            '.length-picker input[type="radio"]:checked:not(.toggle-radio)',
          );
          variantSelects.querySelectorAll("#selected-length").forEach((span) => {
            if (lengthCheckedInput) {
              span.textContent = " " + lengthCheckedInput.value;
              span.style.display = "inline";
            } else {
              span.style.display = "none";
            }
          });
        }
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks,
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateVariantInputsUpdate(variant?.id, variant);
          this.attemptAutoSubmit(variant?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = () => false,
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            if (id === "product-update-price") {
              const destinations = this.querySelectorAll(
                ".product-upate-price-flex",
              );
              if (source && destinations.length) {
                destinations.forEach((dest) => {
                  dest.innerHTML = source.innerHTML;
                });
                const sampleBtn = destinations[0].querySelector(
                  ".update-button-quick-modal",
                );
                const newVariantId = sampleBtn?.dataset.variantId;
                const oldVariantDiv = this.querySelector("#old-variant-id");
                const oldVariantId = oldVariantDiv
                  ? oldVariantDiv.textContent.trim()
                  : null;
                // Enable if variant changed (ignore if new variant exists in cart)
                const shouldEnable =
                  newVariantId && oldVariantId && newVariantId !== oldVariantId;
                this.querySelectorAll(".update-button-quick-modal").forEach(
                  (btn) => {
                    if (shouldEnable) {
                      btn.disabled = false;
                      btn.classList.remove("disabled");
                    } else {
                      btn.disabled = true;
                      btn.classList.add("disabled");
                    }
                  },
                );
              }
              return;
            }
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`,
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("product-update-price");

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id, variant?.id);
          this.updateWishlist();

          updateSourceFromDestination("price");
          updateSourceFromDestination("product-price");

          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden"),
          );
          updateSourceFromDestination("Sku-Accordion-Main", ({ classList }) =>
            classList.contains("hidden"),
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === "",
          );

          updateSourceFromDestination("QuantityDropdown");

          const qtyWrapper = this.querySelector(
            `#QuantityDropdown-${this.dataset.section}`,
          );
          if (qtyWrapper) {
            const select = qtyWrapper.querySelector(
              `select#Quantity-${this.dataset.section}`,
            );
            if (select) {
              const stock = parseInt(
                select.dataset.stock || select.options.length || 0,
                10,
              );
              select.disabled = stock <= 0;
              select.classList.toggle("qtyDistabledDropdown", stock <= 0);
            }
          }

          this.updateQuantityRules(this.sectionId, html);

          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden"),
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`,
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`,
          )?.classList.remove("hidden");

          const sourceSubmitButton = html.querySelector(
            '.product-form__submit[name="add"]',
          );
          const shouldDisableSubmit =
            sourceSubmitButton?.hasAttribute("disabled") ?? true;
          const submitButtonText =
            sourceSubmitButton?.querySelector("span")?.textContent?.trim() ||
            window.variantStrings.soldOut;

          this.productForms.forEach((form) =>
            form.toggleSubmitButton(
              shouldDisableSubmit,
              submitButtonText,
            ),
          );

          const newAddBagHtml = html.querySelector(
            ".product-form__buy-buttons.add-bag",
          )?.innerHTML;
          if (newAddBagHtml) {
            this.querySelectorAll(".product-form__buy-buttons.add-bag").forEach(
              (div) => {
                div.innerHTML = newAddBagHtml;
              },
            );
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(".product-variant-id").forEach((input) => {
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateVariantInputsUpdate(variantId, variant) {
        // Select all product-info components inside the modal
        const input = this.querySelector("#variant-id-input");

        if (input) {
          // if (input.value !== String(variantId)) {
          input.value = variantId ?? "";
          // }
        }

        const wishlistBtn = this.querySelector(".wishlist-btn"); // "Add to List" button
        const addedBtn = this.querySelector(".remove-list"); // "Added to List!" button

        if (wishlistBtn && variantId) {
          wishlistBtn.dataset.variantId = variantId;
        }

        let wishlist = JSON.parse(localStorage.getItem("myWishlist")) || [];
        const selectedColor = String(variant?.option1 || "")
          .trim()
          .toLowerCase();
        const productId = String(wishlistBtn?.dataset.productId || "");

        // Show correct button on load
        if (
          wishlist.some(
            (item) =>
              String(item.id) === productId &&
              String(item.variantColor || "").trim().toLowerCase() ===
                selectedColor,
          )
        ) {
          if (wishlistBtn) wishlistBtn?.classList?.add("!hidden");
          if (wishlistBtn) addedBtn?.classList?.remove("!hidden");
        } else {
          if (wishlistBtn) wishlistBtn.classList.remove("!hidden");
          if (addedBtn) addedBtn.classList.add("!hidden");
        }
      }

      // updateURL(url, variantId) {
      //   this.querySelector("share-button")?.updateUrl(
      //     `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`
      //   );

      //   if (this.dataset.updateUrl === "false") return;
      //   window.history.replaceState(
      //     {},
      //     "",
      //     `${url}${variantId ? `?variant=${variantId}` : ""}`
      //   );
      // }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`,
        );

        if (this.dataset.updateUrl === "false") return;

        const currentUrl = new URL(window.location.href);
        const hasAppView = currentUrl.searchParams.has("app-view");

        const newUrl = new URL(`${window.location.origin}${url}`);
        if (variantId) newUrl.searchParams.set("variant", variantId);

        // Preserve the app-view=true parameter if it exists
        if (hasAppView) {
          newUrl.searchParams.set(
            "app-view",
            currentUrl.searchParams.get("app-view"),
          );
        }

        // Preserve any other unrelated query params or hash fragments
        currentUrl.searchParams.forEach((value, key) => {
          if (!newUrl.searchParams.has(key) && key !== "variant") {
            newUrl.searchParams.set(key, value);
          }
        });
        newUrl.hash = currentUrl.hash;

        // Update browser history without reloading
        window.history.replaceState({}, "", newUrl.toString());
      }

      setUnavailable() {
        this.productForms.forEach((form) =>
          form.toggleSubmitButton(true, window.variantStrings.soldOut),
        );

        const selectors = [
          "price",
          "Inventory",
          "product-price",
          // Removed "product-update-price" from this list (fixes button visibility; it's now always updated via HTML replacement)
          "Sku",
          "Sku-Accordion-Main",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
          "QuantityDropdown",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        this.querySelectorAll(selectors).forEach(({ classList }) =>
          classList.add("hidden"),
        );
      }

      updateMedia(html, variantFeaturedMediaId, variantId) {
        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const originalSection =
          this.dataset.originalSection || this.dataset.section;

        const mobilemediaGallerySource = this.querySelector(
          `#swiper-mobile-media-${this.dataset.section}`,
        );
        const mobilemediaGalleryDestination = html.querySelector(
          `#swiper-mobile-media-${originalSection}`,
        );

        if (mobilemediaGallerySource && mobilemediaGalleryDestination) {
          // Replace with ID adjustment to match duplicated IDs in modal
          let newHtml = mobilemediaGalleryDestination.innerHTML.replaceAll(
            originalSection,
            this.dataset.section,
          );
          mobilemediaGallerySource.innerHTML = newHtml;
          requestAnimationFrame(() => {
            rebuildMobileSwiper(this.dataset.section, {
              preserveActiveIndex: false,
              preferredMediaId: variantFeaturedMediaId ?? null,
            });
          });
        }

        const container = html.querySelector("#side-guide-modal-opener");
        const oldcontainer = this.querySelector("#side-guide-modal-opener");

        this.querySelectorAll("#side-guide-modal-opener").forEach(
          (nodeOfModal) => {
            if (nodeOfModal && container) {
              nodeOfModal.innerHTML = container.innerHTML;
            }
          },
        );

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]"),
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId),
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]"),
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
          );
          let shouldRefresh = false;

          // Add new items
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // Remove old items
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // Refresh source if changed
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // Reorder items to match new destination
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);
              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`,
                  ),
                );
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            },
          );
        }

        // --- Active media handling ---
        if (variantFeaturedMediaId) {
          this.querySelector(`media-gallery`)?.setActiveMedia?.(
            `${this.dataset.section}-${variantFeaturedMediaId}`,
            true,
          );
        } else {
          // If no variant media, reset first slide as active
          const firstSlide = this.querySelector("media-gallery ul li");
          if (firstSlide) {
            this.querySelectorAll("media-gallery ul li").forEach((slide) =>
              slide.classList.remove("is-active"),
            );
            firstSlide.classList.add("is-active");
          }
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`,
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`,
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;

        // ✅ update thumbnail list
        const thumbnailSource = this.querySelector(
          `#ThumbnailList-${this.dataset.section}`,
        );
        const thumbnailDestination = html.querySelector(
          `#ThumbnailList-${originalSection}`,
        );

        if (thumbnailSource && thumbnailDestination) {
          thumbnailSource.innerHTML = thumbnailDestination.innerHTML;
        }

        const modal = document.querySelector(".quick-add-modal[open]");
        if (!modal) return; // no modal is open

        if (modal) {
          const selectorkey = `#ThumbnailList-${this.dataset.section}`.replace(
            "quickadd-",
            "",
          );

          const thumbnailSourcemodal = modal.querySelector(
            `#ThumbnailList-${this.dataset.section}`,
          );

          const thumbnailDestination = html.querySelector(selectorkey);
          if (thumbnailSourcemodal && thumbnailDestination) {
            thumbnailSourcemodal.innerHTML = thumbnailDestination.innerHTML;
          }

          if (thumbnailSourcemodal) {
            // ✅ Add "is-active" to the matching li based on featured media (instead of always first)
            const thumbs = thumbnailSourcemodal.querySelectorAll("li");
            thumbs.forEach((li) => li.classList.remove("is-active"));
            let activeThumb;
            if (variantFeaturedMediaId) {
              activeThumb = thumbnailSourcemodal.querySelector(
                `li[data-media-id="${variantFeaturedMediaId}"]`,
              );
            }
            if (!activeThumb) {
              activeThumb = thumbs[0];
            }
            if (activeThumb) {
              activeThumb.classList.add("is-active");
            }
          }
          const oldVariantDiv = this.querySelector("#old-variant-id");
          const oldVariantId = oldVariantDiv
            ? oldVariantDiv.textContent.trim()
            : null;

          // Consolidating button logic: disable if new variant matches original cart item
          const updateBtn = modal.querySelector(".update-button-quick-modal");
          if (updateBtn && oldVariantId) {
            // Button is only enabled if:
            // 1. A valid variant is selected
            // 2. That variant ID is NOT the same as the one we started with
            const shouldEnable =
              variantId && String(variantId) !== String(oldVariantId);

            updateBtn.disabled = !shouldEnable;
            updateBtn.classList.toggle("disabled", !shouldEnable);
            updateBtn.classList.toggle("opacity-50", !shouldEnable);
            updateBtn.classList.toggle("cursor-not-allowed", !shouldEnable);
          }
        }
      }
      updateWishlist() {
        const labels = document.querySelectorAll(".swatch-input__label");

        labels.forEach((label) => {
          label.addEventListener("click", function () {
            // Get the color name from the visually-hidden span (exclude sold-out span)
            const colorNameSpan = this.querySelector(
              ".visually-hidden:not(.label-unavailable)",
            );
            const colorName = colorNameSpan
              ? colorNameSpan.textContent.trim()
              : "Unknown";

            // Optionally get the variant ID from the associated input
            const inputId = this.getAttribute("for");
            const input = document.getElementById(inputId);
            const variantId = input ? input.dataset.variantId : "Unknown";

            const wishlistBtn = this.querySelector(".wishlist-btn"); // "Add to List" button
            const addedBtn = this.querySelector(".remove-list"); // "Added to List!" button

            if (!wishlistBtn && !addedBtn) return;

            let wishlist = JSON.parse(localStorage.getItem("myWishlist")) || [];

            // Show correct button on load
            if (wishlist.some((item) => item.variantId == variantId)) {
              if (wishlistBtn) wishlistBtn.classList.add("!hidden");
              if (wishlistBtn) addedBtn.classList.remove("!hidden");
            } else {
              if (wishlistBtn) wishlistBtn.classList.remove("!hidden");
              if (addedBtn) addedBtn.classList.add("!hidden");
            }
          });
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner",
        ).classList.remove("hidden");
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner",
            ).classList.add("hidden"),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`,
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === ".quantity__label") {
              const updatedAriaLabelledBy =
                updated.getAttribute("aria-labelledby");
              if (updatedAriaLabelledBy) {
                current.setAttribute("aria-labelledby", updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = this.querySelector(`#${labelId}`);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent =
                    updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForms() {
        return this.querySelectorAll(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products",
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list",
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`,
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}


document.addEventListener('DOMContentLoaded', () => {

  // Helper: Should we open modal?
  function shouldOpenStickyModal() {
    const sizePicker = document.querySelector('.size-picker');
    const lengthPicker = document.querySelector('.length-picker');

    const sizeSelected = sizePicker ? 
      !!document.querySelector('.size-picker input[type="radio"]:checked:not(.toggle-radio)') : true;

    const lengthSelected = lengthPicker ? 
      !!document.querySelector('.length-picker input[type="radio"]:checked') : true;

    return !sizeSelected || !lengthSelected;
  }

  // Sticky button click handler
  const stickyButtons = document.querySelectorAll(
    '.sticky-add, [id*="sticky"] button[name="add"], .sticky-add-modal button'
  );

  stickyButtons.forEach(button => {
    button.addEventListener('click', function (e) {

      const needsModal = shouldOpenStickyModal();

      if (needsModal) {
        console.log("Size or Length missing → Opening StickyVariantModal");
        // Let modal-opener do its job
      } else {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();

        const mainForm = document.querySelector('product-form form') || 
                         document.querySelector('form[data-type="add-to-cart-form"]');
        if (mainForm) {
          mainForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }
      console.groupEnd();

    }, { capture: true });
  });
});
